import React, { useCallback, useRef, useState } from 'react'
import { IconButton, Tooltip } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FaPause, FaPlay } from 'react-icons/fa6'

interface Props {
    recordingUrl: string
    size?: 'xs' | 'sm' | 'md'
}

/**
 * Play/pause toggle for a call recording. Streams on demand (preload="none")
 * so call tables and chat timelines don't fetch audio until staff hit play.
 * State syncs both ways with the <audio> element (ended/pause/play events).
 */
export default function CallRecordingButton({
    recordingUrl,
    size = 'sm',
}: Props) {
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const [isPlaying, setIsPlaying] = useState(false)

    const togglePlayback = useCallback(() => {
        const audio = audioRef.current
        if (!audio) return

        if (isPlaying) {
            audio.pause()
        } else {
            audio.play()
        }
        setIsPlaying(!isPlaying)
    }, [isPlaying])

    return (
        <>
            <audio
                ref={audioRef}
                src={recordingUrl}
                preload="none"
                onEnded={() => setIsPlaying(false)}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
            />
            <Tooltip
                label={isPlaying ? 'Pause recording' : 'Play recording'}
                hasArrow
            >
                <IconButton
                    aria-label={
                        isPlaying ? 'Pause recording' : 'Play recording'
                    }
                    icon={<Icon as={isPlaying ? FaPause : FaPlay} />}
                    size={size}
                    variant="ghost"
                    colorScheme="brand"
                    rounded="full"
                    onClick={togglePlayback}
                />
            </Tooltip>
        </>
    )
}
